<script setup lang="ts">
// `DataExportStep2.vue:1-519` + `:733-929`, minus the date half — that is `ExportDateRangePanel`,
// mounted below rather than re-ported (`:268-310`, `:520-732`).
//
// Legacy held 13 props and 11 emits and pushed every edit up to `DataExportCreate.vue`; this step
// reads and writes `store.draft` directly. The template catalog is loaded HERE so the four states
// contract §14 requires are rendered — legacy's `getTemplates` swallowed the failure into a
// `console.error` and left an empty select (`DataExportCreate.vue:457-461`).
import { computed, ref, watch } from 'vue'

import { useExportColumns } from '../../../composables/useExportColumns'
import { useExportDateRange } from '../../../composables/useExportDateRange'
import type { ExportDateRangeSelection } from '../../../models/export.types'
import type {
  ExportBreakdown,
  ExportColumnSelection,
  ExportFilterDraft,
  ExportPlatform,
} from '../../../models/exportJob.types'
import { useDataExportStore } from '../../../stores/dataExportStore'
import ExportDateRangePanel from '../ExportDateRangePanel.vue'
import ExportFilterPanel from '../ExportFilterPanel.vue'
import MetricConfigPanel from '../MetricConfigPanel.vue'

import { toReportColumnOptions } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { strIncludes } from '@/shared/lib/string'
import { Alert, Button, Checkbox, Input, MultiSelect, Select, Skeleton } from '@/shared/ui'
import type { SelectOption } from '@/shared/ui/select'

const props = withDefaults(defineProps<{ availableEvents?: readonly string[] }>(), {
  availableEvents: () => [],
})

const store = useDataExportStore()
const { t } = useI18n()
const {
  customTemplates,
  templateId,
  dimensions,
  metrics,
  selectedColumns,
  breakdownOptions: offeredBreakdowns,
  isSelected,
  isMandatory,
  selectTemplate,
  toggleColumn,
  setColumns,
} = useExportColumns(store)

type LoadState = 'loading' | 'error' | 'ready'
// The catalog is already in the store when this step mounts: the Source step's `next()` gate loads
// it to answer "does this platform selection have any template at all?" and refuses to advance when
// it does not (`useExportWizard.leaveSource`). Starting at `loading` and loading again on mount
// therefore issued a second, identical request for a catalog the step already had (ORP-20205).
const loadState = ref<LoadState>('ready')
const searchDimension = ref('')
const searchMetric = ref('')

const platforms = computed<readonly ExportPlatform[]>(() => store.draft?.platforms ?? [])
const templateOptions = computed<SelectOption<string>[]>(() =>
  customTemplates.value.map((template) => ({
    value: template.identifier,
    label: template.visibleHeader || template.name,
  })),
)
const isEmpty = computed(() => loadState.value === 'ready' && templateOptions.value.length === 0)
const filteredDimensions = computed(() => search(dimensions.value, searchDimension.value))
const filteredMetrics = computed(() => search(metrics.value, searchMetric.value))
const sortOptions = computed<SelectOption<string>[]>(() => toReportColumnOptions(metrics.value))
const sortOrderOptions = computed<SelectOption<number>[]>(() => [
  { value: -1, label: t('dataExport.data.sort.desc') },
  { value: 1, label: t('dataExport.data.sort.asc') },
])
const breakdownOptions = computed<SelectOption<string>[]>(() =>
  offeredBreakdowns.value.map((breakdown) => ({
    value: breakdown.identifier,
    label: breakdown.name,
  })),
)
const breakdownValue = computed(() =>
  (store.draft?.breakdowns ?? []).map((breakdown) => breakdown.identifier),
)
const filters = computed<ExportFilterDraft[]>({
  get: () => store.draft?.filters ?? [],
  set: (value) => store.updateDraft({ filters: value }),
})
// The draft carries the DERIVED range; the panel edits the SELECTION. `useExportDateRange` is the
// one owner of the conversion — this step re-reads the selection out of the derived value rather
// than keeping a second copy of it.
const dateSelection = computed<ExportDateRangeSelection>({
  get: (): ExportDateRangeSelection => {
    const range = store.draft?.dateRange
    if (!range) return { option: 'custom' }
    return {
      option: range.option,
      ...(range.amount === undefined ? {} : { amount: range.amount }),
      customStart: range.firstDate,
      customEnd: range.lastDate,
    }
  },
  set: (selection: ExportDateRangeSelection) =>
    store.updateDraft({ dateRange: useExportDateRange(selection) }),
})

function search(
  list: readonly ExportColumnSelection[],
  query: string,
): readonly ExportColumnSelection[] {
  return query ? list.filter((column) => strIncludes(column.header, query)) : list
}

async function loadTemplates(): Promise<void> {
  loadState.value = 'loading'
  try {
    await store.loadTemplates(platforms.value)
    loadState.value = 'ready'
  } catch {
    // The `ApiError` already reached here from the store; the step owns the retryable state.
    loadState.value = 'error'
  }
}

function setBreakdowns(identifiers: string[]): void {
  const next = identifiers
    .map((identifier) => offeredBreakdowns.value.find((item) => item.identifier === identifier))
    .filter((option): option is ExportBreakdown => option !== undefined)
  store.updateDraft({ breakdowns: next })
}

function setSortField(field: string | null): void {
  if (!store.draft) return
  store.updateDraft({ sort: { ...store.draft.sort, field } })
}

function setSortOrder(order: number | null): void {
  if (!store.draft) return
  store.updateDraft({ sort: { ...store.draft.sort, order } })
}

// Not `immediate` — see `loadState`. A platform selection edited while this step stays mounted
// still reloads, and the error state's Retry calls the same loader.
watch(platforms, () => void loadTemplates(), { deep: true })

defineExpose({ loadTemplates })
</script>

<template>
  <section class="export-data-step grid gap-lg" :aria-label="t('dataExport.data.title')">
    <div v-if="loadState === 'loading'" data-testid="data-step-loading">
      <Skeleton :lines="4" />
    </div>

    <Alert
      v-else-if="loadState === 'error'"
      tone="danger"
      :title="t('dataExport.data.templates.errorTitle')"
      data-testid="data-step-error"
    >
      {{ t('dataExport.data.templates.errorBody') }}
      <Button variant="soft" size="sm" @click="loadTemplates()">
        {{ t('dataExport.data.templates.retry') }}
      </Button>
    </Alert>

    <Alert
      v-else-if="isEmpty"
      tone="info"
      :title="t('dataExport.data.templates.emptyTitle')"
      data-testid="data-step-empty"
    >
      {{ t('dataExport.data.templates.emptyBody') }}
    </Alert>

    <template v-else>
      <Select
        :model-value="templateId"
        :options="templateOptions"
        :label="t('dataExport.data.templates.label')"
        :placeholder="t('dataExport.data.templates.placeholder')"
        :empty-label="t('dataExport.common.noResults')"
        searchable
        @update:model-value="$event && selectTemplate($event)"
      />

      <div
        class="export-data-step__lists grid grid-cols-[repeat(auto-fit,_minmax(15rem,_1fr))] gap-md"
      >
        <div class="export-data-step__list grid gap-xs content-start" data-testid="dimension-list">
          <h3 class="export-data-step__list-title text-sm font-semibold">
            {{ t('dataExport.data.dimensions') }}
          </h3>
          <Input
            v-model="searchDimension"
            type="search"
            :label="t('dataExport.data.searchDimensions')"
            hide-label
            :placeholder="t('dataExport.data.searchPlaceholder')"
          />
          <ul
            class="export-data-step__options max-h-72 overflow-y-auto p-xs border border-border rounded-sm"
          >
            <li v-for="column in filteredDimensions" :key="column.field">
              <Checkbox
                :model-value="isSelected(column.field)"
                :label="column.header"
                :disabled="isMandatory(column.field)"
                @update:model-value="toggleColumn(column)"
              />
            </li>
          </ul>
        </div>

        <div class="export-data-step__list grid gap-xs content-start" data-testid="metric-list">
          <h3 class="export-data-step__list-title text-sm font-semibold">
            {{ t('dataExport.data.metrics') }}
          </h3>
          <Input
            v-model="searchMetric"
            type="search"
            :label="t('dataExport.data.searchMetrics')"
            hide-label
            :placeholder="t('dataExport.data.searchPlaceholder')"
          />
          <ul
            class="export-data-step__options max-h-72 overflow-y-auto p-xs border border-border rounded-sm"
          >
            <li v-for="column in filteredMetrics" :key="column.field">
              <Checkbox
                :model-value="isSelected(column.field)"
                :label="column.header"
                :disabled="isMandatory(column.field)"
                @update:model-value="toggleColumn(column)"
              />
            </li>
          </ul>
        </div>
      </div>

      <MetricConfigPanel
        :columns="selectedColumns"
        :available-events="props.availableEvents"
        @update:columns="setColumns"
      />

      <ExportFilterPanel v-model="filters" :columns="selectedColumns" />

      <MultiSelect
        :model-value="breakdownValue"
        :options="breakdownOptions"
        :label="t('dataExport.data.breakdowns')"
        :placeholder="t('dataExport.data.breakdownPlaceholder')"
        :empty-label="t('dataExport.common.noResults')"
        searchable
        @update:model-value="setBreakdowns"
      />

      <div class="export-data-step__sort grid grid-cols-2 gap-sm">
        <Select
          :model-value="store.draft?.sort.field ?? null"
          :options="sortOptions"
          :label="t('dataExport.data.sort.metricLabel')"
          :empty-label="t('dataExport.common.noResults')"
          clearable
          searchable
          @update:model-value="setSortField"
        />
        <Select
          :model-value="store.draft?.sort.order ?? null"
          :options="sortOrderOptions"
          :label="t('dataExport.data.sort.orderLabel')"
          :empty-label="t('dataExport.common.noResults')"
          @update:model-value="setSortOrder"
        />
      </div>

      <ExportDateRangePanel v-model="dateSelection" />
    </template>
  </section>
</template>
